import { Temporal } from 'temporal-polyfill'

import { normalizeTimeZone } from '@mangovoice/constants'
import type { AnswerRateSegment } from '@mangovoice/types'

const DEFAULT_REPORTING_TIMEZONE = 'UTC'

const padDatePart = (value: number): string => String(value).padStart(2, '0')

const getResolvedTimeZone = (timeZone?: string): string =>
  normalizeTimeZone(timeZone || DEFAULT_REPORTING_TIMEZONE)

const toUtcIsoString = (zonedDateTime: Temporal.ZonedDateTime): string =>
  new Date(zonedDateTime.epochMilliseconds).toISOString()

const toTemporalInstant = (date: Date | string): Temporal.Instant => {
  if (typeof date === 'string') {
    return Temporal.Instant.from(date)
  }

  return Temporal.Instant.from(date.toISOString())
}

const toZonedDateTime = (
  date: Date | string,
  timeZone?: string,
): Temporal.ZonedDateTime =>
  toTemporalInstant(date).toZonedDateTimeISO(getResolvedTimeZone(timeZone))

const formatZonedDateTime = (zonedDateTime: Temporal.ZonedDateTime): string =>
  `${zonedDateTime.year}-${padDatePart(zonedDateTime.month)}-${padDatePart(zonedDateTime.day)} ${padDatePart(zonedDateTime.hour)}:${padDatePart(zonedDateTime.minute)}:${padDatePart(zonedDateTime.second)}`

/**
 * A Date whose local fields are `timeZone`'s current wall clock, for code
 * that reads local fields (like the date-range presets) as that zone's.
 * Inverse of `wallClockToUtcIso`.
 */
export function getWallClockNowInTimeZone(timeZone: string): Date {
  const now = Temporal.Now.plainDateTimeISO(getResolvedTimeZone(timeZone))
  return new Date(
    now.year,
    now.month - 1,
    now.day,
    now.hour,
    now.minute,
    now.second,
    now.millisecond,
  )
}

/** The UTC instant at which `timeZone`'s clock shows `wallClock`. */
export function wallClockToUtcIso(
  wallClock: Temporal.PlainDateTime,
  timeZone: string,
): string {
  return wallClock
    .toZonedDateTime(getResolvedTimeZone(timeZone))
    .toInstant()
    .toString({ smallestUnit: 'millisecond' })
}

export function toLocalDateTimeString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

export function toUtcDateTimeString(date: Date): string {
  return date.toISOString()
}

export function toUtcRfc3339DateTimeString(date: Date): string {
  return date.toISOString()
}

export function getUtcDateRangeForTimeZone(
  afterDate: Date,
  beforeDate: Date,
  timeZone?: string,
): {
  startDateUtc: string
  endDateUtc: string
} {
  const resolvedTimeZone = getResolvedTimeZone(timeZone)

  const startZonedDateTime = Temporal.ZonedDateTime.from({
    timeZone: resolvedTimeZone,
    year: afterDate.getFullYear(),
    month: afterDate.getMonth() + 1,
    day: afterDate.getDate(),
    hour: 0,
    minute: 0,
    second: 0,
  })

  const endZonedDateTime = Temporal.ZonedDateTime.from({
    timeZone: resolvedTimeZone,
    year: beforeDate.getFullYear(),
    month: beforeDate.getMonth() + 1,
    day: beforeDate.getDate(),
    hour: 23,
    minute: 59,
    second: 59,
  })

  return {
    startDateUtc: toUtcIsoString(startZonedDateTime),
    endDateUtc: toUtcIsoString(endZonedDateTime),
  }
}

const toWallClock = (date: Date): Temporal.PlainDateTime =>
  Temporal.PlainDateTime.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds(),
  })

// Compares calendar dates rather than hours: a local Date can't hold a
// midnight the viewer's zone skips, so that start of day arrives as 01:00.
const isLocalStartOfDay = (date: Date): boolean =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() ===
  date.getTime()

/**
 * Reads the local fields of `afterDate` and `beforeDate` as `timeZone`'s wall
 * clock and widens them to whole hours, since the API buckets by hour.
 * `beforeDate` is exclusive, so the range ends one second before it.
 */
export function getUtcHourRangeForTimeZone(
  afterDate: Date,
  beforeDate: Date,
  timeZone?: string,
): {
  startDateUtc: string
  endDateUtc: string
} {
  const resolvedTimeZone = getResolvedTimeZone(timeZone)

  const start = (
    isLocalStartOfDay(afterDate)
      ? toWallClock(afterDate).toPlainDate().toPlainDateTime()
      : toWallClock(afterDate).round({
          smallestUnit: 'hour',
          roundingMode: 'floor',
        })
  ).toZonedDateTime(resolvedTimeZone)

  const endBoundary = toWallClock(beforeDate)
    .round({ smallestUnit: 'hour', roundingMode: 'ceil' })
    .toZonedDateTime(resolvedTimeZone)

  return {
    startDateUtc: toUtcIsoString(start),
    endDateUtc: toUtcIsoString(endBoundary.subtract({ seconds: 1 })),
  }
}

export function formatUtcDateTimeForTimeZone(
  date: Date | string,
  timeZone?: string,
): string {
  try {
    return formatZonedDateTime(toZonedDateTime(date, timeZone))
  } catch (error) {
    console.error('Error formatting UTC date for timezone:', error)
    return typeof date === 'string' ? date : date.toISOString()
  }
}

export function bucketSegmentsByDay(
  segments: AnswerRateSegment[],
  timeZone: string,
): AnswerRateSegment[] {
  const resolvedTimeZone = getResolvedTimeZone(timeZone)
  const dayBuckets = new Map<string, { answered: number; rejected: number }>()

  for (const segment of segments) {
    if (!segment.dateRange?.start) continue

    const zoned = Temporal.Instant.from(
      segment.dateRange.start,
    ).toZonedDateTimeISO(resolvedTimeZone)

    const dayKey = `${zoned.year}-${padDatePart(zoned.month)}-${padDatePart(zoned.day)}`

    const existing = dayBuckets.get(dayKey) ?? { answered: 0, rejected: 0 }
    existing.answered += segment.counts.answered ?? 0
    existing.rejected += segment.counts.rejected ?? 0
    dayBuckets.set(dayKey, existing)
  }

  return Array.from(dayBuckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, counts]) => {
      const [year, month, day] = dayKey.split('-').map(Number)

      const startOfDay = Temporal.ZonedDateTime.from({
        timeZone: resolvedTimeZone,
        year,
        month,
        day,
        hour: 0,
        minute: 0,
        second: 0,
      })

      const endOfDay = Temporal.ZonedDateTime.from({
        timeZone: resolvedTimeZone,
        year,
        month,
        day,
        hour: 23,
        minute: 59,
        second: 59,
      })

      return {
        dateRange: {
          start: toUtcIsoString(startOfDay),
          end: toUtcIsoString(endOfDay),
        },
        counts,
      }
    })
}

export function getIanaTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.error('Error getting timezone:', error)
    return 'America/New_York'
  }
}
